import {
  type GetPicklistOptionSelectionFuzzyMatch,
  type GetPicklistOptionSelectionMatchOptions,
  PicklistSelectionMatchType,
  type PicklistSelectionMatchCandidate,
  type PicklistSelectionMatchCandidates,
} from './picklistSelectionDecision.shared';
import { resolveAdvisorCommandSegmentMatch } from '../../picklist/utils';
import {
  PICKLIST_COMMAND_ALIAS_SET_LOWER,
  PICKLIST_COMMAND_WORD_SET_LOWER,
} from '../NvoqQueue/utils/commandUtils';

/**
 * Resolves exact and (guarded) fuzzy candidates for stable and advisor text, plus match metadata
 * needed for decision-making and telemetry.
 */
export const resolvePicklistSelectionMatchCandidates = ({
  text,
  picklistId,
  allowTriggerlessOptionNameMatch,
  normalizedAdvisorText,
  getPicklistOptionSelectionMatch,
  getFuzzyMatch,
}: {
  text: string;
  picklistId?: string | null;
  allowTriggerlessOptionNameMatch?: boolean;
  normalizedAdvisorText: string;
  getPicklistOptionSelectionMatch: (
    text: string,
    options?: GetPicklistOptionSelectionMatchOptions
  ) => Nullable<PicklistSelectionMatchCandidate>;
  getFuzzyMatch: GetPicklistOptionSelectionFuzzyMatch;
}): PicklistSelectionMatchCandidates => {
  // Run exact matching first for both engines; fuzzy is only a fallback when both exact paths fail.
  const picklistCommandMatch =
    picklistId == null
      ? getPicklistOptionSelectionMatch(text)
      : getPicklistOptionSelectionMatch(text, { picklistId, allowTriggerlessOptionNameMatch });
  const advisorExactMatch =
    picklistId != null && normalizedAdvisorText !== ''
      ? resolveAdvisorCommandSegmentMatch({
          normalizedAdvisorText,
          picklistId,
          allowTriggerlessOptionNameMatch,
          commandWordSet: PICKLIST_COMMAND_WORD_SET_LOWER,
          commandAliasSet: PICKLIST_COMMAND_ALIAS_SET_LOWER,
          getPicklistOptionSelectionMatch,
        })
      : null;

  // Guardrail: only consider fuzzy matching when BOTH engines failed exact matching.
  const shouldUseFuzzy = picklistCommandMatch == null && advisorExactMatch == null;
  const stableFuzzyMatch = shouldUseFuzzy
    ? picklistId == null
      ? getFuzzyMatch(text)
      : getFuzzyMatch(text, { picklistId, allowTriggerlessOptionNameMatch })
    : null;
  const advisorFuzzyMatch =
    shouldUseFuzzy && picklistId != null && normalizedAdvisorText !== ''
      ? getFuzzyMatch(normalizedAdvisorText, {
          picklistId,
          allowTriggerlessOptionNameMatch,
        })
      : null;

  const stableSelectionMatch = (picklistCommandMatch ??
    stableFuzzyMatch) as PicklistSelectionMatchCandidate | null;
  const advisorSelectionMatch = (advisorExactMatch ??
    advisorFuzzyMatch) as PicklistSelectionMatchCandidate | null;
  const stableMatchType =
    picklistCommandMatch != null
      ? PicklistSelectionMatchType.Exact
      : stableFuzzyMatch != null
        ? PicklistSelectionMatchType.Fuzzy
        : PicklistSelectionMatchType.None;
  const advisorMatchType =
    advisorExactMatch != null
      ? PicklistSelectionMatchType.Exact
      : advisorFuzzyMatch != null
        ? PicklistSelectionMatchType.Fuzzy
        : PicklistSelectionMatchType.None;

  return {
    stableSelectionMatch,
    advisorSelectionMatch,
    stableMatchType,
    advisorMatchType,
    stablePickIndex: stableSelectionMatch?.pickIndex ?? null,
    advisorPickIndex: advisorSelectionMatch?.pickIndex ?? null,
    stableConfidence: picklistCommandMatch != null ? 1 : (stableFuzzyMatch?.confidence ?? 0),
    advisorConfidence: advisorExactMatch != null ? 1 : (advisorFuzzyMatch?.confidence ?? 0),
    stableMargin: picklistCommandMatch != null ? 1 : (stableFuzzyMatch?.margin ?? 0),
    advisorMargin: advisorExactMatch != null ? 1 : (advisorFuzzyMatch?.margin ?? 0),
  };
};
